import { useEffect, useState } from "react";

import { Alert, type Flash } from "../components/Alert";
import { Badge } from "../components/Badge";
import { Card, Empty } from "../components/Card";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Field, Input, Select } from "../components/Field";
import { Modal } from "../components/Modal";
import { PageHead } from "../components/PageHead";
import {
  CELL_ACTIONS,
  CELL_MUTED,
  CELL_STRONG,
  MONO,
  Table,
  TableScroll,
  Tbody,
  Td,
  Thead,
} from "../components/Table";
import {
  createServiceMember,
  fetchMemberDetail,
  fetchMembers,
  fetchRoles,
  grantRole,
  revokeRole,
  setMemberStatus,
} from "../lib/api";
import { messageOf } from "../lib/errors";
import { formatDate } from "../lib/format";
import { PERM, can } from "../lib/rbac";
import { useAction } from "../lib/useAction";
import type {
  CreateServiceMemberInput,
  Member,
  MemberDetail,
  MemberFilter,
  MemberGrant,
  MemberStatus,
  Role,
  Session,
} from "../lib/types";
import { ApiKeyModal } from "./ApiKeyModal";
import { CreateServiceModal } from "./CreateServiceModal";

/**
 * 사용자 관리 (`/members`).
 *
 * Google Workspace 로그인으로 자동 등록(JIT)된 사람과 시스템 발급용 service member 를 함께
 * 다룹니다. 할 수 있는 일은 필터 조회, 역할 부여/회수, 활성/비활성 전환, 그리고 서비스 계정에
 * 한정된 두 가지 — 서비스 계정 추가와 API 키 관리(발급·교체·회수)입니다. 사람은 로그인 JIT 로만
 * 생기므로 "추가" 는 서비스 계정 전용입니다.
 *
 * **자기 계정 비활성화는 막습니다**(self-lockout 방어). 서버도 409 로 거부합니다. 본인 행에는
 * 비활성화 버튼 대신 "본인 계정" 을 글자로 보입니다 — 비활성 버튼의 이유를 `title` 툴팁에만 두면
 * 키보드(비활성 버튼은 포커스되지 않음)·터치 사용자는 이유를 볼 수 없습니다.
 *
 * 변경 동작은 모두 확인 창/모달 안에서 응답까지 진행 상태로 두고, 실패하면 창을 유지한 채 오류를
 * 창 안에 보여줍니다. 성공하면 닫고 화면 상단에 결과를 알립니다.
 */
export function MembersScreen({
  session,
  revision,
  onFlash,
  onRefresh,
}: {
  session: Session;
  revision: number;
  onFlash: (flash: Flash) => void;
  onRefresh: () => void;
}) {
  const [filter, setFilter] = useState<MemberFilter>({ type: "", status: "", q: "" });
  /*
   * 검색어는 확정(`filter.q`)과 입력 중(`qDraft`)을 분리합니다.
   *
   * 원본의 `<input onchange>` 는 DOM `change` 이벤트라 **blur 또는 Enter** 에서만 발생했습니다.
   * React 의 `onChange` 는 키 입력마다 발생하므로 그대로 옮기면 한 글자마다 서버를 때립니다.
   * 그래서 입력은 로컬 상태로 받고 blur/Enter 에서만 확정해 원본의 의미를 보존합니다.
   */
  const [qDraft, setQDraft] = useState("");

  const [members, setMembers] = useState<Member[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roleTarget, setRoleTarget] = useState<{ member: Member; detail: MemberDetail } | null>(
    null,
  );

  const [creating, setCreating] = useState(false);
  const [keyTarget, setKeyTarget] = useState<Member | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ member: Member; status: MemberStatus } | null>(
    null,
  );
  /** 역할 모달을 열기 위해 상세를 받는 중인 member — 그 행의 "역할" 버튼만 진행 상태가 됩니다. */
  const [openingRoles, setOpeningRoles] = useState<number | null>(null);

  const mayAssign = can(session, PERM.roleAssign);
  const mayManageKeys = can(session, PERM.apiKeyIssue);

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetchMembers(filter),
      // Role 카탈로그는 부여 권한이 있을 때만 의미가 있습니다(없으면 403 을 부를 뿐).
      mayAssign ? fetchRoles() : Promise.resolve<Role[]>([]),
    ])
      .then(([memberRows, roleRows]) => {
        if (!alive) return;
        setMembers(memberRows);
        setRoles(roleRows);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(messageOf(err));
      });
    return () => {
      alive = false;
    };
  }, [revision, filter, mayAssign]);

  /** 실패는 그대로 던집니다 — 확인 창이 받아 창 안에 보여줍니다. */
  const onSetStatus = async (member: Member, status: MemberStatus) => {
    await setMemberStatus(member.memberId, status, session.csrfToken);
    const label = status === "disabled" ? "비활성화" : "활성화";
    onFlash({ kind: "ok", text: `${member.displayName} 계정을 ${label}했습니다.` });
    onRefresh();
  };

  /** 역할 모달은 **상세를 먼저 받아** 엽니다 — 목록 응답에는 부여 목록이 없습니다. */
  const onOpenRoles = async (member: Member) => {
    if (openingRoles !== null) return;
    setOpeningRoles(member.memberId);
    try {
      const detail = await fetchMemberDetail(member.memberId);
      if (!detail) throw new Error("역할 정보를 받지 못했습니다.");
      setRoleTarget({ member, detail });
    } catch (err: unknown) {
      onFlash({ kind: "error", text: `역할 조회 실패: ${messageOf(err)}` });
      onRefresh();
    } finally {
      setOpeningRoles(null);
    }
  };

  /**
   * 생성에 성공하면 키 발급 권한이 있을 때 **바로 API 키 모달을 엽니다** — 서비스 계정은 키가
   * 있어야 쓸 수 있으므로 다음 단계로 이어 줍니다. 역할 부여는 목록의 "역할" 에서 따로 합니다.
   * 실패는 그대로 던집니다 — 모달이 받아 창 안에 보여주고 입력을 유지합니다.
   */
  const onCreate = async (input: CreateServiceMemberInput) => {
    const created = await createServiceMember(input, session.csrfToken);
    onFlash({
      kind: "ok",
      text: `${input.displayName} 서비스 계정을 만들었습니다. 필요한 역할을 부여하세요.`,
    });
    setCreating(false);
    if (created && mayManageKeys) setKeyTarget(created);
    onRefresh();
  };

  const onGrant = async (member: Member, roleId: number) => {
    await grantRole(member.memberId, roleId, session.csrfToken);
    onFlash({ kind: "ok", text: `${member.displayName} 에게 역할을 부여했습니다.` });
    setRoleTarget(null);
    onRefresh();
  };

  const onRevoke = async (member: Member, grant: MemberGrant) => {
    await revokeRole(member.memberId, grant.roleId, session.csrfToken);
    onFlash({ kind: "ok", text: `${member.displayName} 의 ${grant.roleName} 역할을 회수했습니다.` });
    setRoleTarget(null);
    onRefresh();
  };

  return (
    <>
      <PageHead
        title="사용자"
        sub="Google Workspace 로그인으로 자동 등록된 구성원과 서비스 계정입니다."
        actions={<Button onClick={() => setCreating(true)}>서비스 계정 추가</Button>}
      />

      {loadError ? <Alert kind="error">{loadError}</Alert> : null}

      <Card>
        <div className="flex flex-wrap gap-[8px] border-b border-grey-100 px-[20px] py-[16px] [&>*]:flex-[1_1_140px] sm:[&>*]:flex-[0_1_auto]">
          <Field id="f-q" label="검색">
            <Input
              id="f-q"
              type="search"
              placeholder="이름 또는 이메일"
              value={qDraft}
              onChange={(ev) => setQDraft(ev.target.value)}
              onBlur={() => setFilter((f) => ({ ...f, q: qDraft.trim() }))}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") setFilter((f) => ({ ...f, q: qDraft.trim() }));
              }}
            />
          </Field>
          <Field id="f-type" label="종류">
            <Select
              id="f-type"
              value={filter.type}
              onChange={(ev) =>
                setFilter((f) => ({ ...f, type: ev.target.value as MemberFilter["type"] }))
              }
              options={[
                { value: "", label: "전체" },
                { value: "human", label: "사람" },
                { value: "service", label: "서비스 계정" },
              ]}
            />
          </Field>
          <Field id="f-status" label="상태">
            <Select
              id="f-status"
              value={filter.status}
              onChange={(ev) =>
                setFilter((f) => ({ ...f, status: ev.target.value as MemberFilter["status"] }))
              }
              options={[
                { value: "", label: "전체" },
                { value: "active", label: "활성" },
                { value: "disabled", label: "비활성" },
              ]}
            />
          </Field>
        </div>

        {members.length === 0 && !loadError ? <Empty>조건에 맞는 사용자가 없습니다.</Empty> : null}

        {members.length > 0 ? (
          <TableScroll>
            <Table>
              <Thead columns={["구성원", "종류", "상태", "소속 시스템", ""]} />
              <Tbody>
                {members.map((m) => (
                  <MemberRow
                    key={m.memberId}
                    member={m}
                    isSelf={m.memberId === session.member.memberId}
                    mayManageKeys={mayManageKeys}
                    onOpenKeys={() => setKeyTarget(m)}
                    openingRoles={openingRoles === m.memberId}
                    onOpenRoles={() => void onOpenRoles(m)}
                    onSetStatus={(status) => setStatusTarget({ member: m, status })}
                  />
                ))}
              </Tbody>
            </Table>
          </TableScroll>
        ) : null}
      </Card>

      {roleTarget ? (
        <RoleModal
          member={roleTarget.member}
          detail={roleTarget.detail}
          roles={roles}
          mayAssign={mayAssign}
          onClose={() => setRoleTarget(null)}
          onGrant={(roleId) => onGrant(roleTarget.member, roleId)}
          onRevoke={(grant) => onRevoke(roleTarget.member, grant)}
        />
      ) : null}

      {statusTarget ? (
        <ConfirmDialog
          title={statusTarget.status === "disabled" ? "계정 비활성화" : "계정 활성화"}
          message={
            statusTarget.status === "disabled"
              ? `${statusTarget.member.displayName} 계정을 비활성화합니다. 로그인과 API 키 사용이 즉시 막힙니다.`
              : `${statusTarget.member.displayName} 계정을 다시 활성화합니다.`
          }
          confirmLabel={statusTarget.status === "disabled" ? "비활성화" : "활성화"}
          tone={statusTarget.status === "disabled" ? "danger" : "primary"}
          onConfirm={() => onSetStatus(statusTarget.member, statusTarget.status)}
          onClose={() => setStatusTarget(null)}
        />
      ) : null}

      {creating ? (
        <CreateServiceModal
          session={session}
          onClose={() => setCreating(false)}
          onSubmit={onCreate}
        />
      ) : null}

      {keyTarget ? (
        <ApiKeyModal session={session} member={keyTarget} onClose={() => setKeyTarget(null)} />
      ) : null}
    </>
  );
}

/** 상태 전환 칸의 고정 폭 — 가장 긴 라벨("비활성화")이 들어가는 폭입니다. */
const STATUS_SLOT = "min-w-[76px]";

function MemberRow({
  member,
  isSelf,
  mayManageKeys,
  openingRoles,
  onOpenKeys,
  onOpenRoles,
  onSetStatus,
}: {
  member: Member;
  isSelf: boolean;
  mayManageKeys: boolean;
  openingRoles: boolean;
  onOpenKeys: () => void;
  onOpenRoles: () => void;
  onSetStatus: (status: MemberStatus) => void;
}) {
  return (
    <tr>
      <Td>
        <div className={CELL_STRONG}>{member.displayName}</div>
        <div className={CELL_MUTED}>{member.email ?? "(이메일 없음)"}</div>
      </Td>
      <Td>
        <Badge>{member.type === "service" ? "서비스 계정" : "사람"}</Badge>
      </Td>
      <Td>
        {member.status === "active" ? (
          <Badge tone="ok">활성</Badge>
        ) : (
          <Badge tone="danger">비활성</Badge>
        )}
      </Td>
      <Td>
        <span className={MONO}>{member.owningSystem ?? "—"}</span>
      </Td>
      <Td>
        <div className={CELL_ACTIONS}>
          {member.type === "service" && mayManageKeys ? (
            <Button variant="ghost" size="sm" onClick={onOpenKeys}>
              API 키
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" pending={openingRoles} onClick={onOpenRoles}>
            역할
          </Button>
          {/*
            상태 전환 칸은 폭을 고정합니다 — "비활성화"/"활성화"/"본인 계정" 이 행마다 달라도 버튼 열이
            흔들리지 않게 합니다. 자기 계정 비활성화는 서버도 409 로 막습니다(self-lockout 방어).
          */}
          {isSelf ? (
            <span className={`${STATUS_SLOT} inline-flex items-center justify-center text-[12px] font-semibold text-grey-400`}>
              본인 계정
            </span>
          ) : member.status === "active" ? (
            <Button
              variant="danger"
              size="sm"
              className={STATUS_SLOT}
              onClick={() => onSetStatus("disabled")}
            >
              비활성화
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className={STATUS_SLOT}
              onClick={() => onSetStatus("active")}
            >
              활성화
            </Button>
          )}
        </div>
      </Td>
    </tr>
  );
}

function RoleModal({
  member,
  detail,
  roles,
  mayAssign,
  onClose,
  onGrant,
  onRevoke,
}: {
  member: Member;
  detail: MemberDetail;
  roles: Role[];
  mayAssign: boolean;
  onClose: () => void;
  /** reject 되면 메시지를 창 안에 보여주고 창을 유지합니다. */
  onGrant: (roleId: number) => Promise<unknown>;
  onRevoke: (grant: MemberGrant) => Promise<unknown>;
}) {
  const [pick, setPick] = useState("");
  const [revoking, setRevoking] = useState<MemberGrant | null>(null);
  const grant = useAction();

  const grants = detail.grants;
  const granted = new Set(grants.map((g) => g.roleId));
  // 이미 가진 역할은 고를 수 없게 빼 둡니다(서버가 중복 부여를 거부하기 전에).
  const assignable = roles.filter((r) => !granted.has(r.id));
  const canGrantMore = mayAssign && assignable.length > 0;

  /** 부여 응답 대기 중에는 닫지 않습니다. */
  const close = () => {
    if (!grant.pending) onClose();
  };

  return (
    <Modal
      title={`${member.displayName} 역할`}
      sub={member.email ?? `member #${String(member.memberId)}`}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={grant.pending} onClick={close}>
            닫기
          </Button>
          {canGrantMore ? (
            <Button
              // 역할을 고르기 전에는 할 일이 없으므로 비활성 — 선택 하나로 결정되는 동작입니다.
              disabled={pick === ""}
              pending={grant.pending}
              onClick={() => void grant.run(() => onGrant(Number(pick)))}
            >
              부여
            </Button>
          ) : null}
        </>
      }
      status={grant.error ? <Alert kind="error">{`역할 부여 실패: ${grant.error}`}</Alert> : null}
    >
      {grants.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-[8px] p-0">
          {grants.map((g) => (
            <li
              key={g.roleId}
              className="flex items-center gap-[10px] rounded-xl border border-grey-100 px-[12px] py-[10px]"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-bold">{g.roleName}</div>
                <div className="text-[11px] text-grey-400">
                  {`${g.roleKey} · ${g.expiresAt ? `만료 ${formatDate(g.expiresAt)}` : "무기한"}`}
                </div>
              </div>
              {mayAssign ? (
                <div className="ml-auto">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={grant.pending}
                    onClick={() => setRevoking(g)}
                  >
                    회수
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <Empty>부여된 역할이 없습니다.</Empty>
      )}

      {canGrantMore ? (
        <div className="mt-[14px] flex flex-col gap-[14px]">
          <Field id="grant-role" label="역할 추가">
            <Select
              id="grant-role"
              value={pick}
              disabled={grant.pending}
              onChange={(ev) => setPick(ev.target.value)}
              options={[
                { value: "", label: "역할 선택" },
                ...assignable.map((r) => ({
                  value: String(r.id),
                  label: `${r.name} (${r.key})`,
                })),
              ]}
            />
          </Field>
        </div>
      ) : null}

      {revoking ? (
        <ConfirmDialog
          title="역할 회수"
          message={`${member.displayName} 의 ${revoking.roleName}(${revoking.roleKey}) 역할을 회수합니다. 이 역할로 받던 권한이 즉시 사라집니다.`}
          confirmLabel="회수"
          onConfirm={() => onRevoke(revoking)}
          onClose={() => setRevoking(null)}
        />
      ) : null}
    </Modal>
  );
}
